import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '@/domains/dashboard-management/components/ConfirmModal/ConfirmModal';
import WrapperMobile from '@/shared/components/layout/WrapperMobile/WrapperMobile';
import HeaderMobile from '@/shared/components/ui/HeaderMobile/HeaderMobile';
import StatusFilter from '@/shared/components/ui/StatusFilter/StatusFilter';
import { showToast } from '@/shared/utils/showToast';
import TopBarHeader from '@/domains/mobile-web/components/TopBarHeader/TopBarHeader';
import { useCartCount } from '@/domains/mobile-web/hooks/useCart';
import {
    useCancelOrder,
    useFinishOrder,
    useOrderPages,
} from '@/domains/mobile-web/hooks/useOrders';
import type { OrderDisplayStatus, TransferStatus } from '@/domains/mobile-web/types/order.types';
import { toTransactionOrder } from '@/domains/mobile-web/utils/order';
import CardProductTransaction, {
    type TransactionOrder,
} from '@/domains/mobile-web/components/CardProductTransaction/CardProductTransaction';

// transfer_reported is not a stored status but the reported bank transfers the admin still checks
type FilterId = 'all' | OrderDisplayStatus | 'transfer_reported';

// Menunggu Pembayaran and Menunggu Konfirmasi split the unpaid orders by whether the transfer is reported
const FILTER_QUERY: Partial<Record<FilterId, { status: OrderDisplayStatus; transferStatuses: TransferStatus[] }>> = {
    pending: { status: 'pending', transferStatuses: ['awaiting_transfer', 'rejected'] },
    transfer_reported: { status: 'pending', transferStatuses: ['reported'] },
};

// Dikembalikan has no tab; those orders show under Semua
const BOOK_FILTERS: { id: FilterId; label: string }[] = [
    { id: 'all', label: 'Semua' },
    { id: 'pending', label: 'Menunggu Pembayaran' },
    { id: 'transfer_reported', label: 'Menunggu Konfirmasi' },
    { id: 'processing', label: 'Diproses' },
    { id: 'shipped', label: 'Dikirim' },
    { id: 'arrived', label: 'Barang Sampai' },
    { id: 'completed', label: 'Selesai' },
    { id: 'cancelled', label: 'Dibatalkan' },
];

// One request per typed word rather than per keystroke
const SEARCH_DEBOUNCE_MS = 300;

const notAvailableYet = () =>
    showToast({ type: 'info', title: 'Belum tersedia', message: 'Fitur ini belum dapat digunakan.' });

const TransactionHistory = () => {
    const navigate = useNavigate();
    const cartCount = useCartCount();
    const [selectedStatus, setSelectedStatus] = useState<FilterId>('all');

    const [keyword, setKeyword] = useState('');
    const [search, setSearch] = useState('');
    useEffect(() => {
        const timer = setTimeout(() => setSearch(keyword.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [keyword]);

    const query = FILTER_QUERY[selectedStatus];
    const { data, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } = useOrderPages(
        query?.status ?? (selectedStatus === 'all' ? undefined : (selectedStatus as OrderDisplayStatus)),
        search,
        query?.transferStatuses
    );
    const transactions = (data?.pages.flatMap((page) => page.rows) ?? []).map(toTransactionOrder);
    const finishOrder = useFinishOrder();
    const cancelOrder = useCancelOrder();
    const [cancelling, setCancelling] = useState<TransactionOrder | null>(null);

    // Loads the next page when the end of the list scrolls into view
    const sentinelRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const node = sentinelRef.current;
        if (!node || !hasNextPage || typeof IntersectionObserver === 'undefined') return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting && !isFetchingNextPage) fetchNextPage();
            },
            { rootMargin: '200px' }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    const handleReceived = (trx: TransactionOrder) => {
        if (!finishOrder.isPending) finishOrder.mutate(trx.id);
    };

    const handlePay = (trx: TransactionOrder) => navigate(`/store/payment?id=${trx.id}`);

    const confirmCancel = () => {
        if (cancelling) cancelOrder.mutate(cancelling.id, { onSettled: () => setCancelling(null) });
    };

    return (
        <main className="min-h-screen">
            <HeaderMobile>
                <TopBarHeader
                    type="textBucket"
                    onBack={() => navigate(-1)}
                    totalProduct={cartCount}
                    onAction={() => navigate('/store/cart')}
                    title="Riwayat Transaksi"
                />
            </HeaderMobile>

            <WrapperMobile>
                <input
                    type="text"
                    className="input-db-text"
                    placeholder="Cari nomor invoice, judul buku..."
                    aria-label="Cari nomor invoice atau judul buku"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                />

                <div className="overflow-x-auto gap-3 inline-flex">
                    <StatusFilter
                        items={BOOK_FILTERS}
                        value={selectedStatus}
                        onChange={(value) => setSelectedStatus(value as FilterId)}
                    />
                </div>

                {isLoading ? (
                    <p className="body1 text-text-grey text-center py-6">Memuat transaksi...</p>
                ) : isError ? (
                    <p className="body1 text-text-grey text-center py-6">Gagal memuat transaksi.</p>
                ) : transactions.length === 0 ? (
                    <p className="body1 text-text-grey text-center py-6">
                        Belum ada transaksi.
                    </p>
                ) : (
                    <>
                        {transactions.map((trx) => (
                            <CardProductTransaction
                                key={trx.id}
                                transaction={trx}
                                onPay={handlePay}
                                onCancel={setCancelling}
                                onRefund={notAvailableYet}
                                onTrack={(order) => navigate(`/store/track/${order.id}`)}
                                onReceived={handleReceived}
                                onBuyAgain={() => navigate('/store/book')}
                            />
                        ))}

                        {hasNextPage && <div ref={sentinelRef} aria-hidden="true" />}
                    </>
                )}
            </WrapperMobile>

            <ConfirmModal
                open={cancelling !== null}
                title="Batalkan Pesanan ?"
                message="Apakah Anda yakin ingin membatalkan pesanan ini?"
                confirmLabel="Ya, Batalkan"
                variant="danger"
                loading={cancelOrder.isPending}
                onConfirm={confirmCancel}
                onClose={() => setCancelling(null)}
            />
        </main>
    );
};

export default TransactionHistory;
